import { useContext, useEffect } from 'react';
import { shallowEqual } from 'react-redux';

import { Box } from '@zillow/constellation-10';
import { propertyUtils } from '@zg-rentals/rental-manager-env';

import { useGetConversationCountsForListing } from '../../hooks/queries/leads';
import { LoadingMapContext } from '../../providers/LoadingMapContext/context';

import { useSelector } from '../../selectors/useSelector';

import { updateLoadingState } from '../../providers/LoadingMapContext/actions';

import ConnectedApplications from '../ConnectedApplications';
import ConnectedPropertySummarySheet from '../ConnectedPropertySummarySheet';
import InterestedRenters from '../InterestedRenters';
import { TRIGGER_LOCATIONS } from '../../constants/ClickstreamConstants';
import ConnectedInquiries from '../ConnectedInquiries';
import InquiriesModule from '../InquiriesModule';

type Props = {
  updateMinModuleHeight?: () => void;
};

const ConnectedInterestedRenters = ({ updateMinModuleHeight }: Props) => {
  const { isApplicationsRestricted, isEnterpriseCustomer, listingAlias, isRoomForRent } = useSelector((state) => {
    return {
      isApplicationsRestricted:
        state?.properties?.currentProperty?.restrictions?.applications?.isAccessRestricted || false,
      isEnterpriseCustomer: state.user.isEnterpriseCustomer,
      listingAlias: state.properties.propertyOverview.listingAlias,
      isRoomForRent: propertyUtils.isRoomForRent({ currentProperty: state.properties.currentProperty }),
    };
  }, shallowEqual);

  const { data, isLoading } = useGetConversationCountsForListing(listingAlias);
  const {
    reducerDispatch,
    loadingByComponent: { ConnectedInquiries: isConnectedInquiriesLoading },
  } = useContext(LoadingMapContext);

  const displayInterestedRenters = isRoomForRent === false;

  useEffect(() => {
    updateLoadingState({
      reducerDispatch,
      data: {
        conversationCounts: isLoading,
      },
    });
  }, [isLoading]);

  const renderInterestedRenters = displayInterestedRenters ? <InterestedRenters limit={5} /> : null;

  return (
    <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '600' }}>
      <Box
        css={{
          order: 1,
          gridColumnStart: { base: '1', xl: '8', xxl: '9' },
          gridColumnEnd: { base: 'span 12', xl: 'span 5', xxl: 'span 4' },
        }}
      >
        {isEnterpriseCustomer ? (
          <>
            {isConnectedInquiriesLoading === false && data?.totalConversationsCount === 0 ? (
              <ConnectedInquiries updateMinModuleHeight={updateMinModuleHeight} />
            ) : (
              <InquiriesModule />
            )}
          </>
        ) : (
          renderInterestedRenters
        )}
      </Box>

      <Box
        css={{
          order: { base: 2, xl: 1 },
          gridRowStart: { base: '', xl: '1' },
          gridColumnStart: '1',
          gridColumnEnd: {
            base: 'span 12',
            xl: isEnterpriseCustomer || displayInterestedRenters ? 'span 7' : 'span 12',
            xxl: isEnterpriseCustomer || displayInterestedRenters ? 'span 8' : 'span 12',
          },
        }}
      >
        <ConnectedApplications
          updateMinModuleHeight={updateMinModuleHeight}
          triggerLocation={TRIGGER_LOCATIONS.IRENE_APPLICATIONS_DASHBOARD}
        />
      </Box>

      {isApplicationsRestricted === false && (
        <Box css={{ order: 3, gridColumnStart: '1', gridColumnEnd: 'span 12' }}>
          <ConnectedPropertySummarySheet />
        </Box>
      )}
    </Box>
  );
};
export default ConnectedInterestedRenters;
